import { Col, Container, Row } from "react-grid-system";
import { StyledJobPage } from "./JobPage.styles";
import { useParams } from "react-router";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useStoreActions, useStoreState } from "../../store";
import { HeaderMode } from "../../types/HeaderMode";
import { IJobDetails } from "../../types/IJobDetails";
import {
  getJobDetails,
  getJobs,
  getProjectFolder,
  getSimilarJobs,
} from "../../services/ApiService";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { useAuth } from "../../utility/useAuth";
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  QueryCellInfoEventArgs,
} from "@syncfusion/ej2-react-grids";
import { IComponent } from "../../types/IComponent";
import { IRouting } from "../../types/IRouting";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import {
  MenuComponent,
  MenuEventArgs,
  MenuItemModel,
} from "@syncfusion/ej2-react-navigations";
import { IJobAction, JobAction } from "../../components/JobAction/JobAction";
import signalRService from "../../services/signalRService";
import { JobActionType } from "../../types/JobActionType";
import { addOrUpdateJob } from "../../utility/jobHelper";
import { IJobActionMessage } from "../../types/IJobActionMessage";
import { User } from "oidc-client";
import { IsComponentValid } from "../../utility/component";
import {
  DisplayText,
  QRCodeGeneratorComponent,
} from "@syncfusion/ej2-react-barcode-generator";

export const JobPage: React.FC = () => {
  const navigate = useNavigate();
  const { jobNumber } = useParams();

  const { jobs } = useStoreState((state) => state.appSettings);
  const { setJobs, setJobNumber, setHeaderMode, setSimilarJobs } =
    useStoreActions((actions) => actions.appSettings);

  const auth = useAuth();
  const refJobAction = useRef<IJobAction>(null);
  const [jobDetails, setJobDetails] = useState<IJobDetails>();
  const [userToken, setUser] = useState<User | null>(null);

  useEffect(() => {
    auth.getUser().then((user) => {
      setUser(user);
    });
  }, []);

  function loadJobs(warehouse?: string) {
    getJobs(auth, warehouse)
      .then((data) => {
        setJobs(data);
      })
      .catch((error) => {
        const message = `Failed to get jobs. Error: ${error}`;
        console.error(`Failed to get jobs. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      });
  }

  useEffect(() => {
    if (userToken) {
      signalRService.startConnection(userToken);

      signalRService.onReceiveMessage((receivedMessage) => {
        console.log(`Message received: ${JSON.stringify(receivedMessage)}`);

        const jobActionMessage: IJobActionMessage = JSON.parse(receivedMessage);

        if (jobActionMessage) {
          if (
            jobActionMessage.jobAction === JobActionType.JobsReloaded.toString()
          ) {
            loadJobs();
          } else if (
            jobActionMessage.jobAction ===
            JobActionType.CompleteProductionOrder.toString()
          ) {
            const filteredJobs = [
              ...(jobs?.filter(
                (job) => job.jobNumber !== jobActionMessage.jobNumber,
              ) ?? []),
            ];
            setJobs(filteredJobs);
          } else {
            const filteredJobs = [
              ...addOrUpdateJob(jobActionMessage.job, jobs ?? []),
            ];
            setJobs(filteredJobs);
          }
        }

        if (jobNumber === jobActionMessage.jobNumber) {
          getJobDetails(auth, jobNumber)
            .then((data) => {
              setJobDetails(data);
            })
            .catch((error) => {
              const message = `Failed to get job details. Error: ${error}`;
              console.error(`Failed to get job details. Error: ${error}`);
              ToastUtility.show(message, "Error", 2000);
            });
        }
      });
    }

    return () => {
      if (signalRService.connection) {
        console.log(`stopping connection`);
        signalRService.connection.stop();
      }
    };
  }, [userToken]);

  useEffect(() => {
    if (jobNumber) {
      setJobNumber(jobNumber);
      setHeaderMode(HeaderMode.JobDetails);
      getJobDetails(auth, jobNumber)
        .then((data) => {
          setJobDetails(data);
        })
        .catch((error) => {
          const message = `Failed to get job details. Error: ${error}`;
          console.error(`Failed to get job details. Error: ${error}`);
          ToastUtility.show(message, "Error", 2000);
        });
    }
  }, [jobNumber]);

  const customizeComponentCell = (args: QueryCellInfoEventArgs) => {
    if (args.cell && args.data) {
      const component = args.data as IComponent;

      if (!IsComponentValid(component)) {
        args.cell.classList.add("component-bad");
      } else {
        args.cell.classList.add("component-ok");
      }
    }
  };

  const customizeRoutingCell = (
    args: QueryCellInfoEventArgs,
    currentRoutingNumber: number,
  ) => {
    if (args.cell && args.data) {
      const routing = args.data as IRouting;

      if (routing.routingNumber === currentRoutingNumber) {
        args.cell.classList.add("routing-current");
      } else if (routing.routingNumber < currentRoutingNumber) {
        args.cell.classList.add("routing-done");
      } else {
        args.cell.classList.add("routing-due");
      }
    }
  };

  const menuItems: MenuItemModel[] = [
    { id: "back", text: "Back", iconCss: "e-icons e-back" },
    { id: "startOperation", text: "Start", iconCss: "e-icons e-play" },
    { id: "stopOperation", text: "Stop", iconCss: "e-icons e-pause" },
    {
      id: "completeOperation",
      text: "Complete",
      iconCss: "e-icons e-check-tick",
    },
    { id: "reworkOperation", text: "Rework", iconCss: "e-icons e-undo" },
    { id: "viewBom", text: "BOM", iconCss: "e-icons e-edit" },

    { id: "addNotes", text: "Notes", iconCss: "e-icons e-comment-show" },
    { id: "printLabel", text: "Print", iconCss: "e-icons e-print" },
    {
      text: "Documents & Images",
      items: [
        {
          id: "viewProjectFolder",
          text: "Project Folders",
          iconCss: "e-icons e-group-1",
        },
        {
          id: "addImages",
          text: "Add Images",
          iconCss: "e-icons e-image",
        },
        {
          id: "showSimilarImages",
          text: "Show Similar Images",
          iconCss: "e-icons e-image",
        },
        {
          id: "addDocuments",
          text: "Add Documents",
          iconCss: "e-icons e-file-document",
        },
      ],
    },
    { separator: true },
    {
      id: "holdProductionOrder",
      text: "Hold Production Order",
      iconCss: "e-icons e-thumbs-down",
    },
    {
      id: "completeProductionOrder",
      text: "Production Order Complete",
      iconCss: "e-icons e-changes-accept",
    },
  ];

  const completedOperationMenuItems: MenuItemModel[] = [
    { id: "back", text: "Back", iconCss: "e-icons e-back" },
    { id: "viewBom", text: "BOM", iconCss: "e-icons e-edit" },
    { id: "addNotes", text: "Notes", iconCss: "e-icons e-comment-show" },
    {
      text: "Documents & Images",
      items: [
        {
          id: "viewProjectFolder",
          text: "Project Folders",
          iconCss: "e-icons e-group-1",
        },
        {
          id: "addImages",
          text: "Add Images",
          iconCss: "e-icons e-image",
        },
        {
          id: "showSimilarImages",
          text: "Show Similar Images",
          iconCss: "e-icons e-image",
        },
        {
          id: "addDocuments",
          text: "Add Documents",
          iconCss: "e-icons e-file-document",
        },
      ],
    },
    { separator: true },
    { id: "printLabel", text: "Print", iconCss: "e-icons e-print" },
  ];

  const rejectedOperationMenuItems: MenuItemModel[] = [
    { id: "back", text: "Back", iconCss: "e-icons e-back" },
    { id: "reworkOperation", text: "Rework", iconCss: "e-icons e-undo" },
    { id: "viewBom", text: "BOM", iconCss: "e-icons e-edit" },
    {
      text: "Documents & Images",
      items: [
        {
          id: "viewProjectFolder",
          text: "Project Folders",
          iconCss: "e-icons e-group-1",
        },
        {
          id: "addImages",
          text: "Add Images",
          iconCss: "e-icons e-image",
        },
        {
          id: "showSimilarImages",
          text: "Show Similar Images",
          iconCss: "e-icons e-image",
        },
        {
          id: "addDocuments",
          text: "Add Documents",
          iconCss: "e-icons e-file-document",
        },
      ],
    },
    { separator: true },
    { id: "addNotes", text: "Notes", iconCss: "e-icons e-comment-show" },
    { id: "printLabel", text: "Print", iconCss: "e-icons e-print" },
  ];

  const onHoldMenuItems: MenuItemModel[] = [
    { id: "back", text: "Back", iconCss: "e-icons e-back" },
    { id: "viewBom", text: "BOM", iconCss: "e-icons e-edit" },

    { id: "addNotes", text: "Notes", iconCss: "e-icons e-comment-show" },
    { id: "printLabel", text: "Print", iconCss: "e-icons e-print" },
    {
      text: "Documents & Images",
      items: [
        {
          id: "viewProjectFolder",
          text: "Project Folders",
          iconCss: "e-icons e-group-1",
        },
        {
          id: "addImages",
          text: "Add Images",
          iconCss: "e-icons e-image",
        },
        {
          id: "showSimilarImages",
          text: "Show Similar Images",
          iconCss: "e-icons e-image",
        },
        {
          id: "addDocuments",
          text: "Add Documents",
          iconCss: "e-icons e-file-document",
        },
      ],
    },
    { separator: true },
    {
      id: "unholdProductionOrder",
      text: "Unhold Production Order",
      iconCss: "e-icons e-thumbs-up-1",
    },
  ];

  const toolTips = new Map<string, string>([
    ["startOperation", "Start Operation"],
    ["stopOperation", "Stop Operation"],
    ["completeOperation", "Complete Operation"],
    ["reworkOperation", "Rework Operation"],
    ["viewBom", "View Bill of Materials"],
    ["viewProjectFolder", "View Project Folders"],
    ["addNotes", "Add Notes"],
    ["printLabel", "Print CS Packing Label"],
    ["completeProductionOrder", "Complete Production Order"],
  ]);

  function beforeItemRender(args: MenuEventArgs): void {
    args.element.setAttribute("title", toolTips.get(args.item.id ?? "") ?? "");
  }

  function selectMenuItem(args: MenuEventArgs): void {
    const id = args.item.id;

    const job = jobs?.find((job) => job.jobNumber === jobNumber);

    if (!job) {
      return;
    }

    if (id === "viewJobDetails") {
      navigate(`/job/${jobNumber}`);
    } else if (id === "viewBom") {
      navigate(`/bom/${jobNumber}`);
    } else if (id === "addImages") {
      navigate(`/job-images/${job.jobNumber}`);
    } else if (id === "addDocuments") {
      navigate(`/job-documents/${job.jobNumber}`);
    }
    if (id === "showSimilarImages") {
      getSimilarJobs(auth, job.jobNumber)
        .then((jobs) => {
          if (jobs && jobs.length > 0) {
            setSimilarJobs(jobs);
            navigate(`/similar-jobs-images`);
          } else {
            const message = `No similar jobs found`;
            ToastUtility.show(message, "Info", 2000);
          }
        })
        .catch((error) => {
          const message = `Failed to get similar jobs. Error: ${error}`;
          ToastUtility.show(message, "Error", 2000);
        });
    } else if (id === "viewProjectFolder") {
      getProjectFolder(auth, job.jobNumber)
        .then((folderPath) => {
          if (folderPath && folderPath.length > 0) {
            window.open(folderPath, "_blank");
          } else {
            const message = `No Project Folder  found for ${job.jobNumber} job`;
            ToastUtility.show(message, "Info", 2000);
          }
        })
        .catch((error) => {
          const message = `Failed to get Project Folder path. Error: ${error}`;
          ToastUtility.show(message, "Error", 2000);
        });
    } else if (id === "startOperation") {
      refJobAction.current?.startJob(job, false);
    } else if (id === "stopOperation") {
      refJobAction.current?.stopJob(job);
    } else if (id === "reworkOperation") {
      refJobAction.current?.reworkJob(job);
    } else if (id === "completeOperation") {
      refJobAction.current?.completeJob(job);
    } else if (id === "completeProductionOrder") {
      refJobAction.current?.completeProductionOrder(job);
    } else if (id === "holdProductionOrder") {
      refJobAction.current?.holdProductionOrder(job);
    } else if (id === "unholdProductionOrder") {
      refJobAction.current?.unholdProductionOrder(job);
    } else if (id === "addNotes") {
      refJobAction.current?.addJobNote(job);
    } else if (id === "printLabel") {
      refJobAction.current?.printLabel(job);
    } else if (id === "back") {
      navigate(-1);
    }
  }

  return (
    <StyledJobPage>
      <JobAction key="jobPageAction" ref={refJobAction} />
      {jobDetails ? (
        <Container fluid>
          <Row align="stretch">
            <MenuComponent
              items={
                jobDetails.onHold
                  ? onHoldMenuItems
                  : jobDetails?.operation === "QUAR"
                    ? rejectedOperationMenuItems
                    : jobDetails?.operation === "COMPLETED"
                      ? completedOperationMenuItems
                      : menuItems
              }
              select={selectMenuItem}
              beforeItemRender={beforeItemRender}
            />
          </Row>
          <Row align="stretch">
            <Col>Stock Code:</Col>
            <Col>
              <TextBoxComponent value={jobDetails.stockCode} readOnly />
            </Col>
            <Col>
              <QRCodeGeneratorComponent
                id="barcode"
                width={"75px"}
                height={"75px"}
                displayText={{ visibility: false }}
                value={jobDetails.stockCode?.toUpperCase()}
              ></QRCodeGeneratorComponent>
            </Col>
            <Col>Description:</Col>
            <Col>
              <TextBoxComponent
                value={jobDetails.description}
                readOnly
                multiline
              />
            </Col>
            <Col>Quantity Ordered:</Col>
            <Col>
              <TextBoxComponent
                value={jobDetails.qtyOrdered.toString()}
                readOnly
              />
            </Col>
          </Row>
          <Row align="stretch">
            <Col>
              <h2>Routing and Operations</h2>
              <GridComponent
                dataSource={jobDetails.routings}
                height={320}
                allowSelection={false}
                gridLines="Both"
                queryCellInfo={(a) => {
                  customizeRoutingCell(a, jobDetails.routingNumber);
                }}
                allowTextWrap={true}
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="operation"
                    headerText="Operation"
                    width="100"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="userName"
                    headerText="Associate"
                    width="100"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="requiredMinutes"
                    headerText="Req. Minutes"
                    width="150"
                    textAlign="Center"
                  />
                  <ColumnDirective
                    field="loggedMinutes"
                    headerText="Logged Minutes"
                    width="150"
                    textAlign="Center"
                  />
                  <ColumnDirective
                    field="operDateCompleted"
                    headerText="Comp. Date"
                    width="250"
                    type="datetime"
                    format="d/M/y HH:mm"
                    textAlign="Left"
                  />
                </ColumnsDirective>
              </GridComponent>
            </Col>
            <Col>
              <h2>Components</h2>
              <GridComponent
                dataSource={jobDetails.components}
                height={320}
                gridLines="Both"
                queryCellInfo={customizeComponentCell}
                allowTextWrap={true}
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="line"
                    headerText="Line #"
                    width="75"
                    maxWidth="75"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="componentPart"
                    headerText="Component part number"
                    width="200"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="description"
                    headerText="Part Description"
                    width="100"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="plannedQty"
                    headerText="PlannedQty"
                    width="150"
                    maxWidth="150"
                    textAlign="Center"
                  />
                  <ColumnDirective
                    field="issuedQty"
                    headerText="IssuedQty"
                    width="150"
                    maxWidth="150"
                    textAlign="Center"
                  />
                  <ColumnDirective
                    field="materialStatus"
                    headerText="Material Status"
                    width="150"
                    maxWidth="150"
                    textAlign="Center"
                  />
                  {/*  <ColumnDirective field='pickList' headerText='PickList' width='150' maxWidth='150' textAlign="Left"/>
                    <ColumnDirective field='fulfillmentCode' headerText='FulfillmentCode' width='150' maxWidth='150' textAlign="Left"/> */}
                </ColumnsDirective>
              </GridComponent>
            </Col>
          </Row>
          <Row align="stretch">
            <Col>
              <h2>Logging</h2>
              <GridComponent
                dataSource={jobDetails.loggedHours}
                height={320}
                gridLines="Both"
                allowTextWrap={true}
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="operation"
                    headerText="Operation"
                    width="150"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="associate"
                    headerText="Associate"
                    width="100"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="loginDate"
                    headerText="Login"
                    type="datetime"
                    format="dd/MM/y HH:mm:ss"
                    width="100"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="logoutDate"
                    headerText="Logout"
                    type="datetime"
                    format="dd/MM/y HH:mm:ss"
                    width="100"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="minutes"
                    headerText="Minutes"
                    width="100"
                    textAlign="Center"
                  />
                </ColumnsDirective>
              </GridComponent>
            </Col>
            <Col>
              <h2>Notes</h2>
              <GridComponent
                dataSource={jobDetails.comments}
                height={320}
                gridLines="Both"
                allowTextWrap={true}
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="key"
                    headerText="Type"
                    width="100"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="commentNote"
                    headerText="Comment"
                    textAlign="Left"
                  />
                </ColumnsDirective>
              </GridComponent>
            </Col>
          </Row>
        </Container>
      ) : (
        <>Loading...</>
      )}
    </StyledJobPage>
  );
};
